import type { ZodError } from "zod";

const FIELD_LABELS: Record<string, string> = {
  name: "Nom affiché",
  defaultClientPrice: "Prix client",
  visibilityMode: "Mode de visibilité",
  requireInput: "Saisie requise",
  requiresFile: "Fichier requis",
  requiresMultipleFiles: "Fichiers multiples",
  dynamicPricing: "Tarification dynamique",
  fileType: "Type de fichier",
  active: "Actif",
  partnerAssignments: "Partenaires",
  displayName: "Nom affiché partenaire",
  clientPrice: "Prix client partenaire",
  organizationId: "Organisation",
  selected: "Sélection",
  enabled: "Activé",
};

type FormatZodErrorOptions = {
  mode?: "boundary" | "form";
  maxIssues?: number;
};

const labelForPath = (path: (string | number)[]): string => {
  if (path.length === 0) return "données";

  const parts = path.map((segment) => {
    if (typeof segment === "number") return `ligne ${segment + 1}`;
    return FIELD_LABELS[segment] ?? segment;
  });

  return parts.join(" › ");
};

export function formatZodError(error: ZodError, options: FormatZodErrorOptions = {}): string {
  const mode = options.mode ?? "boundary";
  const maxIssues = options.maxIssues ?? (mode === "form" ? 6 : 20);

  const missingFields: string[] = [];
  const invalidFields: string[] = [];

  for (const issue of error.issues.slice(0, maxIssues)) {
    const fieldName = labelForPath(issue.path);
    const isMissing =
      issue.code === "invalid_type" &&
      ("received" in issue ? issue.received === "undefined" || issue.received === "null" : false);

    if (isMissing || issue.code === "too_small") {
      missingFields.push(fieldName);
    } else {
      invalidFields.push(issue.message ? `${fieldName} — ${issue.message}` : fieldName);
    }
  }

  const remaining = error.issues.length - maxIssues;
  let message =
    mode === "form"
      ? "Certains champs empêchent l'envoi du formulaire.\n\n"
      : "Les informations sont incomplètes ou invalides.\n\n";

  if (missingFields.length > 0) {
    message += `Champs manquants :\n${missingFields.map((f) => `• ${f}`).join("\n")}\n\n`;
  }

  if (invalidFields.length > 0) {
    message += `Champs invalides :\n${invalidFields.map((f) => `• ${f}`).join("\n")}\n\n`;
  }

  if (remaining > 0) {
    message += `… et ${remaining} autre${remaining > 1 ? "s" : ""} problème${remaining > 1 ? "s" : ""}.\n\n`;
  }

  if (mode === "boundary") {
    message +=
      "Veuillez contacter le support avec une capture d'écran de cette erreur si le problème persiste.";
  } else {
    message += "Corrigez les champs indiqués puis réessayez.";
  }

  return message.trim();
}
